import React, {useContext, useEffect, useId, useMemo, useState} from "react";
import IdentityImage from "../../components/IdentityImage.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {fas} from "@fortawesome/free-solid-svg-icons";
import Button from "../../components/Button.jsx";
import {useTranslation} from "react-i18next";
import YesNo from "../../components/YesNo.jsx";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import {ProjectApi, ProjectPermission} from "../../api/authority/generated/index";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import Badge from "../../components/Badge.jsx";
import Select from "react-select";
import {useIsAdministrator} from "../../components/auth/rolesAndPermissions.js";
import ActionButton from "../../components/ActionButton.jsx";

export default function ProjectUser({project, projectRoles, projectUser}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    const [isOwner, setIsOwner] = useState(projectUser.isOwner === true);

    let toggleOwnerId = useId();

    let {context} = useContext(GlobalContext);

    let [deleteQuestionVisible, setDeleteQuestionVisible] = useState(false);

    let [deleteState, setDeleteDownloading, setDeleteResult, setDeleteError] = useDownloadState();
    let [deleted, setDeleted] = useState(false);

    let {getAuthorityConfiguration} = useContext(GlobalContext)

    let removeUser = () => {
        setDeleteDownloading(true);

        new ProjectApi(getAuthorityConfiguration())
            .removeProjectUser({
                projectId: project.id,
                removeUserInput: {
                    userId: projectUser.user.id,
                }
            })
            .then(() => {
                setDeleted(true);
                toast.success(t('project.user-successfully-removed'))
            })
            .catch((e) => et(e).then(toast.error))
            .finally(() => setDeleteDownloading(false))
        ;
    };

    let isAdministrator = useIsAdministrator();

    let canDelete = isAdministrator || (project.isOwner && !projectUser.isOwner);

    let canToggleOwner = isAdministrator || (
        project.isOwner && context.currentIdentity?.user.id !== projectUser.user.id
    );

    let canChangeRole = isAdministrator || project.isOwner;

    const [changeOwner, setChangeOwner] = useState(null);

    let projectRoleValues = useMemo(() => {
        let result = [
            {
                label: t('project.no-role'),
                value: null,
            }
        ];

        for(let role of projectRoles) {
            result.push({
                value: role.id,
                label: role.name,
            })
        }

        return result;
    }, [project.id, projectRoles]);

    useEffect(() => {

        if(changeOwner === null) {
            return;
        }

        new ProjectApi(getAuthorityConfiguration())
            .updateProjectOwner({
                projectId: project.id,
                userId: projectUser.user.id,
                setOwnerInput: {
                    isOwner: changeOwner,
                }
            })
            .then(() => { setChangeOwner(null); })
            .catch((e) => et(e).then(toast.error));

    }, [changeOwner]);


    const [changeRole, setChangeRole] = useState(undefined);

    useEffect(() => {

        if(changeRole === undefined) {
            return;
        }

        new ProjectApi(getAuthorityConfiguration())
            .updateProjectRole({
                projectId: project.id,
                userId: projectUser.user.id,
                setRoleInput: {
                    roleId: changeRole
                }
            })
            .then(() => { setChangeRole(undefined); })
            .catch((e) => et(e).then(toast.error));

    }, [changeRole]);

    const [roleValue, setRoleValue] = useState(null);

    let defaultRoleValue = useMemo(
        () => {
            for(let possibleDefaultValue of projectRoleValues) {
                if(possibleDefaultValue.value === (projectUser.role?.id ?? null)) {
                    setRoleValue(possibleDefaultValue);
                    return possibleDefaultValue;
                }
            }

            return null;
        },
        [projectUser.role?.id, projectRoleValues]
    );

    return (
        <div
            className={`comp__userListUser dark:bg-slate-800 text-slate-800 dark:text-slate-100 ${deleted ? 'hidden' : ''}`}>
            <LoadingComponent
                loading={deleteState.downloading || (changeOwner !== null) || (changeRole !== undefined)}
            >
                <div className={'user-row flex justify-between gap-6 px-3 py-1'}>
                    <div className={'col-image-name w-3/12'}>
                        <div className={'flex items-center text-sm'}>
                            <IdentityImage
                                url={projectUser.user.avatarUrl}
                                user={projectUser.user}
                                className={'mr-2'}
                            />
                            <div className="col-name">
                                <div className="font-medium">
                                    {projectUser.user.firstName} {projectUser.user.lastName}
                                </div>
                                <div className="text-slate-500 text-xs">{projectUser.user.role}</div>
                            </div>
                        </div>
                    </div>

                    <div className={'col-role w-2/12 my-auto text-center'}>
                        {canToggleOwner
                            && <Badge color={isOwner ? 'green' : 'slate'}>
                                <input
                                    type={"checkbox"}
                                    className={'form-checkbox'}
                                    id={toggleOwnerId}
                                    checked={isOwner}
                                    onChange={(e) => {
                                        setIsOwner(!isOwner);
                                        setChangeOwner(!isOwner);
                                    }}
                                />&nbsp;&nbsp;
                                <label htmlFor={toggleOwnerId} className={'select-none'}>{t('project.owner')}</label>
                            </Badge>}
                        {!canToggleOwner
                            && projectUser.isOwner
                            && <Badge color={'green'}>{t('project.owner')}</Badge>
                        }

                        {!canChangeRole && !isOwner && <Badge color={'slate'}>{roleValue?.label}</Badge>}
                    </div>


                    <div className={'col-mail w-3/12 my-auto text-[13px] text-center'}>
                        {canChangeRole && isOwner && <div className={'text-gray-500'}>{t('project.owner-needs-no-role')}</div>}
                        {canChangeRole && !isOwner && <Select
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            options={projectRoleValues}
                            value={roleValue}
                            onChange={(option) => {
                                setRoleValue(option);
                                // noinspection JSUnresolvedReference
                                setChangeRole(option.value);
                            }}
                        />}
                    </div>

                    <div className={'col-mail w-3/12 my-auto text-[13px]'}>{projectUser.user.email}</div>

                    <div className={'col-action w-1/12 my-auto'}>
                        <div className="flex justify-end">
                            {canDelete &&
                                <ActionButton
                                    icon={'trash'}
                                    variant={'primary-red'}
                                    title={t('project.remove-user')}
                                    onClick={() => setDeleteQuestionVisible(true)}
                                />
                            }
                        </div>
                        {canDelete &&
                            <YesNo
                                title={t('confirmation')}
                                question={t('project.really-remove-user-question', {userName: projectUser.user.firstName + " " + projectUser.user.lastName})}
                                visible={deleteQuestionVisible}
                                setVisible={setDeleteQuestionVisible}
                                onYes={removeUser}
                            />
                        }
                    </div>
                </div>
            </LoadingComponent>
        </div>
    );
}
